import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';
import { normalizeFileName } from '@/modules/common/utils/file.ts';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { InfoItem } from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import { useTransferSetCompletedMutation } from '@/modules/issuer-dashboard/queries/transfers.tsx';
import { Button, CalendarInput, CalendarInputProps, ModalContentWrapper, TextInput } from 'ui';
import { TransferPaginateResponse } from 'api';

export interface IssuerConfirmTransferModalProps extends Partial<TransferPaginateResponse> {
  open: boolean;
  onClose: () => void;
}

export const IssuerConfirmTransferModal = (props: IssuerConfirmTransferModalProps) => {
  return (
    <ModalContentWrapper open={props.open} title="Confirm transfer" onClose={props.onClose}>
      <IssuerConfirmTransferData {...props} />
    </ModalContentWrapper>
  );
};

const validationSchema = yup.object({
  confirmation_date: yup.date(),
});

const initialValues = {
  confirmation_date: new Date().toISOString(),
};

export const IssuerConfirmTransferData = ({
  id,
  from_shareholder_user,
  to_shareholder_user,
  register_class,
  quantity,
  memo,
  register_obj,
  price_per_share,
  document_storage_item_urls,
  onClose,
}: IssuerConfirmTransferModalProps) => {
  const { mutateAsync: completeTransfer, isLoading } = useTransferSetCompletedMutation({
    onSuccess: onClose,
  });

  const formik = useFormik({
    initialValues: {
      ...initialValues,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (id) {
        await completeTransfer({ id: id, confirmation_date: values.confirmation_date });
      }
      resetForm();
      onClose();
    },
    enableReinitialize: true,
  });
  const handleChangeDate: CalendarInputProps['onChange'] = date => {
    const isoStringWithOffset = format(
      new Date(date?.toDate().toISOString()),
      "yyyy-MM-dd'T'HH:mm:ssXXX",
    );
    formik.setFieldValue('confirmation_date', isoStringWithOffset);
  };

  const getDisabledDate = (date: Dayjs) => date.isAfter(dayjs().add(1, 'day').endOf('day'));
  // const getDisabledHours = () =>
  //   Array(24)
  //     .fill(null)
  //     .map((_, index) => index)
  //     .filter(h => h >= new Date().getHours() + 1);

  useEffect(() => {
    const isoStringWithOffset = format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXXX");
    formik.setFieldValue('confirmation_date', isoStringWithOffset);
  }, [id]);

  return (
    <div className="w-full flex flex-col">
      <FormikProvider value={formik}>
        <div className="w-full flex flex-col space-y-8">
          <div className="w-full flex items-start gap-6 flex-wrap sm:flex-nowrap">
            <TextInput name="register" label="REGISTER" value={register_obj?.name} disabled />
            <TextInput label="class" name="class" disabled value={register_class?.class_name} />
          </div>
          <div className="w-full flex items-center gap-6 flex-wrap sm:flex-nowrap">
            <TextInput
              label="sender"
              name="sender"
              disabled
              className="capitalize"
              value={from_shareholder_user?.first_name + ' ' + from_shareholder_user?.last_name}
            />
            <TextInput
              label="recipient"
              name="recipient"
              disabled
              className="capitalize"
              value={to_shareholder_user?.first_name + ' ' + to_shareholder_user?.last_name}
            />
          </div>
          <TextInput name="quantity" label="quantity of shares" disabled value={quantity} />
          <TextInput
            name="total"
            label="total"
            disabled
            value={formatNumberWithCommas((quantity || 0) * (price_per_share || 0))}
          />
          <CalendarInput
            disabledDate={getDisabledDate}
            // disabledHours={getDisabledHours}
            defaultValue={dayjs(formik.values.confirmation_date)}
            label="Confirmation date"
            name="confirmation_date"
            onChange={handleChangeDate}
            showTime={true}
            format="DD-MM-YYYY HH:mm"
            error={formik.errors.confirmation_date}
          />
          {document_storage_item_urls?.map((doc, index) => (
            <InfoItem title={`Document #${index + 1}`} key={doc.document_id}>
              <a
                href={doc.document_storage_item_url}
                className="w-[50%] text-end max-w-[300px] text-ellipsis overflow-hidden text-nowrap text-blue-700 hover:underline"
                target="_blank"
              >
                {normalizeFileName(doc.file_name || 'Document')}
              </a>
            </InfoItem>
          ))}
          {memo && (
            <TextInput
              name="memo"
              label="Memo (Optional)"
              disabled
              value={memo}
              textarea
              className="resize-none"
            />
          )}
        </div>
        <Button
          className="mt-6"
          onClick={() => formik.handleSubmit()}
          disabled={isLoading}
          loading={formik.isSubmitting}
        >
          Confirm
        </Button>
      </FormikProvider>
    </div>
  );
};
